import QueueUpload from "./queue-upload.js";
import { uriFromKey, keyFromComponents } from "./paths.js";

/**
 * @typedef {{
 *   queueUpload: any;
 *   api: any;
 *   listController: any;
 *   fileToListPrefixMap: WeakMap<File, string>;
 *   downloadLinkExpireDate: Date;
 *   updateStatus: Function;
 *   state: any;
 *   localizer: any;
 * }} UploadControllerOptions
 */

/** Upload queue and API orchestration controller. */
export default class UploadController {
  /** @param {UploadControllerOptions} options */
  constructor({
    queueUpload,
    api,
    listController,
    fileToListPrefixMap,
    downloadLinkExpireDate,
    updateStatus,
    state,
    localizer,
  }) {
    /**
     * @param {File} file
     */
    queueUpload.onFileStart = async function (file) {
      state.isUploading = true;
      updateStatus(file.name, 0, file.size, false);

      const prefix = fileToListPrefixMap.get(file);
      if (prefix === undefined) {
        throw new Error("");
      }

      if (prefix === listController.listPrefix) {
        listController.addFileToList(
          {
            is: "file",
            key: listController.listPrefix + file.name,
            filename: file.name,
            uploaded: null,
            size: file.size,
            storedSize: api.fileEncryption
              ? await api.encryptedFileSize(file.size)
              : file.size,
            downloadUrl: "",
          },
          "uploading",
          file,
        );
      }

      if (api.fileEncryption) {
        state.encryptingFile = file;
        updateStatus(file.name, 0, file.size, false);
      }
      const uploadFile = api.fileEncryption
        ? await api.encryptFile(file)
        : file;
      if (api.fileEncryption) {
        if (state.encryptingFile !== file) {
          return;
        }
        state.encryptingFile = null;
      }

      if (uploadFile === null) {
        state.isUploading = false;
        updateStatus();
        listController.removePendingFileFromList(file);
        alert(localizer.get("fileUploadEncryptionError", { name: file.name }));
        return false;
      }
      updateStatus(file.name, 0, file.size, false);

      if (uploadFile instanceof File) {
        queueUpload.uploadFileMap.set(file, uploadFile);
      } else {
        queueUpload.uploadFileMap.set(file, {
          size: await api.encryptedFileSize(file.size),
          toStream() {
            return uploadFile;
          },
          async toBlob() {
            const reader = uploadFile.getReader();
            /** @type {Uint8Array<ArrayBuffer>[]} */
            const blobParts = [];
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                break;
              }
              const array = new Uint8Array(value.byteLength);
              array.set(value);
              blobParts.push(array);
            }

            return new Blob(blobParts);
          },
        });
      }

      const uri = uriFromKey(
        keyFromComponents({ prefix, filename: file.name }),
      );
      if (queueUpload.isMultipart(file)) {
        _paq.push(["trackEvent", "QueueUploadMultipart", "Start", file.name]);
        const [status, errorInfo] = await api.fileStore.createMultipartUpload(
          uri,
          file,
        );
        if (!status) {
          state.isUploading = false;
          updateStatus();

          _paq.push([
            "trackEvent",
            "QueueUploadMultipart",
            "StartError",
            file.name,
          ]);

          if (errorInfo) {
            alert(
              localizer.get("fileUploadStartError", { name: file.name }) +
                "\n" +
                localizer.get("errorWithInfo", {
                  code: errorInfo.code,
                  message: errorInfo.message,
                }),
            );
          } else {
            alert(localizer.get("fileUploadStartError", { name: file.name }));
          }
          listController.removePendingFileFromList(file);
          return false;
        }
      }
    };

    /**
     * @param {File} file
     */
    queueUpload.onUploadStart = async function (file) {
      const prefix = fileToListPrefixMap.get(file);
      if (prefix === undefined) {
        throw new Error("");
      }

      const uri = uriFromKey(
        keyFromComponents({ prefix, filename: file.name }),
      );
      const uploadInfo = await api.fileStore.getUploadAuthorizationInfo(
        uri,
        file,
      );

      queueUpload.method = uploadInfo.method;
      queueUpload.headers = uploadInfo.headers;
      queueUpload.headers["Content-Type"] = file.type;
      queueUpload.url = uploadInfo.url;
    };

    /**
     * @param {File} file
     * @param {number} index
     */
    queueUpload.onUploadPartStart = async function (file, index) {
      const prefix = fileToListPrefixMap.get(file);
      if (prefix === undefined) {
        throw new Error("");
      }

      const uri = uriFromKey(
        keyFromComponents({ prefix, filename: file.name }),
      );
      const uploadInfo = await api.fileStore.getUploadPartAuthorizationInfo(
        uri,
        file,
        index + 1,
      );

      queueUpload.method = uploadInfo.method;
      queueUpload.headers = uploadInfo.headers;
      queueUpload.url = uploadInfo.url;
      return uploadInfo;
    };

    // When there is a progress we will update the progress
    queueUpload.onUploadProgress =
      /**
       * @param {File} file
       * @param {XMLHttpRequest|null} xhr
       * @param {number} loaded
       * @param {number} total
       * @param {number} retries
       */
      function (file, xhr, loaded, total, retries) {
        updateStatus(file.name, loaded, total, retries !== 0);
      };

    queueUpload.onUploadPartRetry =
      /**
       * @param {File} file
       * @param {XMLHttpRequest} xhr
       * @param {number} loaded
       * @param {number} total
       * @param {number} retries
       */
      function (file, xhr, loaded, total, retries) {
        _paq.push([
          "trackEvent",
          "QueueUploadMultipart",
          "UploadRetry",
          `${file.name} (${retries.toString()})`,
          QueueUpload.partSizeMultiple,
        ]);
      };

    // When upload is completed we'll process the result from server.
    queueUpload.onUploadComplete =
      /**
       * @param {File} file
       * @param {XMLHttpRequest} xhr
       */
      async function (file, xhr) {
        const apiInfo = api.fileStore.handleUploadComplete(xhr);
        if (!apiInfo.success) {
          _paq.push(["trackEvent", "QueueUpload", "UploadError", file.name]);

          state.isUploading = false;
          updateStatus();

          const errorInfo = apiInfo.errorInfo;
          if (errorInfo) {
            _paq.push([
              "trackEvent",
              "QueueUpload",
              "UploadErrorMessage",
              errorInfo.message,
            ]);

            alert(
              localizer.get("fileUploadError", { name: file.name }) +
                "\n" +
                localizer.get("errorWithInfo", {
                  code: errorInfo.code,
                  message: errorInfo.message,
                }),
            );
          } else {
            _paq.push([
              "trackEvent",
              "QueueUpload",
              "UploadErrorMessage",
              "Unknown",
            ]);

            alert(localizer.get("fileUploadError", { name: file.name }));
          }

          listController.removePendingFileFromList(file);
          return false;
        }

        _paq.push(["trackEvent", "QueueUpload", "UploadSuccess", file.name]);
      };

    queueUpload.onUploadPartComplete =
      /**
       * @param {File} file
       * @param {XMLHttpRequest} xhr
       */
      async function (file, xhr) {
        const apiInfo = api.fileStore.handleUploadPartComplete(xhr);

        if (apiInfo.success) {
          _paq.push([
            "trackEvent",
            "QueueUploadMultipart",
            "UploadSuccess",
            file.name,
            QueueUpload.partSizeMultiple,
          ]);
          return apiInfo.data;
        }

        state.isUploading = false;
        updateStatus();

        const errorInfo = apiInfo.errorInfo;
        if (errorInfo) {
          _paq.push([
            "trackEvent",
            "QueueUploadMultipart",
            "UploadErrorMessage",
            errorInfo.message,
            QueueUpload.partSizeMultiple,
          ]);

          alert(
            localizer.get("fileUploadError", { name: file.name }) +
              "\n" +
              localizer.get("errorWithInfo", {
                code: errorInfo.code,
                message: errorInfo.message,
              }),
          );
        } else {
          _paq.push([
            "trackEvent",
            "QueueUploadMultipart",
            "UploadErrorMessage",
            "Unknown",
            QueueUpload.partSizeMultiple,
          ]);

          alert(localizer.get("fileUploadError", { name: file.name }));
        }

        listController.removePendingFileFromList(file);
        return false;
      };

    /**
     * @param {File} file
     * @param {number} totalPartCount
     * @param {any[]} [partsData]
     */
    queueUpload.onFileComplete = async function (
      file,
      totalPartCount,
      partsData,
    ) {
      const prefix = fileToListPrefixMap.get(file);
      if (prefix === undefined) {
        throw new Error("");
      }

      listController.setPendingFileCancelUploadDisabled(file, true);

      if (queueUpload.isMultipart(file)) {
        if (!partsData) {
          throw new Error("");
        }

        const uri = uriFromKey(
          keyFromComponents({ prefix, filename: file.name }),
        );
        const [status, errorInfo] = await api.fileStore.completeMultipartUpload(
          uri,
          file,
          totalPartCount,
          partsData,
        );
        if (!status) {
          if (errorInfo) {
            _paq.push([
              "trackEvent",
              "QueueUploadMultipart",
              "UploadErrorCompleteMessage",
              errorInfo.message,
            ]);

            alert(
              localizer.get("fileUploadCompleteError", { name: file.name }) +
                "\n" +
                localizer.get("errorWithInfo", {
                  code: errorInfo.code,
                  message: errorInfo.message,
                }),
            );
          } else {
            _paq.push([
              "trackEvent",
              "QueueUploadMultipart",
              "UploadErrorCompleteMessage",
              "Unknown",
            ]);

            alert(
              localizer.get("fileUploadCompleteError", { name: file.name }),
            );
          }

          state.isUploading = false;
          listController.removePendingFileFromList(file);
          updateStatus();

          return;
        }
      }

      state.isUploading = false;
      updateStatus();

      const key = keyFromComponents({ prefix, filename: file.name });

      const downloadUrl = api.fileEncryption
        ? ""
        : await api.fileStore.getDownloadUrl(
            uriFromKey(key),
            downloadLinkExpireDate,
          );

      listController.removePendingFileFromList(file);
      listController.revokeDownloadUrl(key);
      if (prefix === listController.listPrefix) {
        listController.addFileToList({
          is: "file",
          key,
          filename: file.name,
          uploaded: new Date(),
          size: file.size,
          storedSize: api.fileEncryption
            ? await api.encryptedFileSize(file.size)
            : file.size,
          downloadUrl,
        });
      }
    };

    /**
     * @param {File} file
     */
    queueUpload.onCurrentUploadCancelled = async function (file) {
      if (state.encryptingFile === file) {
        state.encryptingFile = null;

        state.isUploading = false;
        updateStatus();
        return;
      }

      if (queueUpload.isMultipart(file)) {
        const prefix = fileToListPrefixMap.get(file);
        if (prefix === undefined) {
          throw new Error();
        }
        const uri = uriFromKey(
          keyFromComponents({ prefix, filename: file.name }),
        );
        await api.fileStore.abortMultipartUpload(uri, file);
      }

      state.isUploading = false;
      updateStatus();
    };
  }
}
